import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../../ui/global/Colors';
import { GithubFormat, SandpackFormat, formatCode, CodeFormat, localTransformCode } from '../utils/formatCode';
import { SandpackProvider, SandpackPreview, SandpackCodeEditor, SandpackConsole, useSandpack } from '@codesandbox/sandpack-react';
import { CONFIG_CODE } from '../utils/codeConfig';
import { GameModes } from '../Game';
import { SaveHandler, SaveButton, setupSaveHandler, triggerSave } from './internal/debugSave';

const SandpackContainer = styled.div<{ $devicePreview?: DeviceType }>`
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background-color: ${Colors.grey9};
    border: 1px solid ${Colors.grey8};
    pointer-events: auto;

    &:focus-within {
        border-radius: 4px;
        border: 1px solid ${Colors.grey6};
    }

    .sp-wrapper {
        width: 100%;
        height: 100%;
        overflow-x: hidden;
        overflow-y: auto;
    }

    .sp-preview {
        display: block;
        border-radius: ${props => props.$devicePreview === 'Mobile' ? '4px' : '0'};
        width: ${props => props.$devicePreview === 'Mobile' ? `${MOBILE.width}px` : '100%'};
        height: ${props => props.$devicePreview === 'Mobile' ? `${MOBILE.height}px` : '100%'};
        ${props => props.$devicePreview === 'Mobile' ? `
            margin: 0 auto;
            overflow: hidden;
        ` : ''}
        pointer-events: auto !important;
    }

    .sp-preview-iframe {
        height: 100% !important;
        pointer-events: auto !important;
    }

    .sp-preview-container {
        pointer-events: auto !important;
    }

    .sp-code-editor {
        width: 100%;
        height: 100%;
        display: block;
        position: relative;
    }

    // In game game and play mode, hide code editor
    &[data-mode='Game'] .sp-code-editor,
    &[data-mode='Play'] .sp-code-editor {
        display: none;
    }

    // In code mode, hide game preview
    &[data-mode='Code'] .sp-preview {
        display: none;
    }

    // In game and play mode, hide code file tabs
    &[data-mode='Game'] .sp-tabs,
    &[data-mode='Play'] .sp-tabs {
        display: none;
    }

    // Ensure game preview fills the entire vertical space
    &[data-mode='Game'] .sp-preview-container,
    &[data-mode='Play'] .sp-preview-container {
        height: 100%;
    }
    .sp-preview-iframe {
        height: 100% !important;
    }

    // Hide Sandpack tooltip - enhanced to target all possible tooltip elements
    .sp-preview-container::before,
    .sp-preview-container::after,
    .sp-preview::before,
    .sp-preview::after,
    .sp-preview-actions,
    .sp-preview-container [data-tooltip],
    .sp-preview [data-tooltip],
    [class*="sp-tooltip"],
    [class*="sandpack-tooltip"] {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
    }

    // Pin tabs to the top
    .sp-tabs {
        position: sticky;
        top: 0;
        z-index: 10;
        // background-color: #1c2022;
        background-color: ${Colors.grey9};

        // Style selected tabs
        .sp-tab-panel,
        .sp-tab-container,
        .sp-tab-button {
            outline: none !important;
            border: none !important;
            box-shadow: none !important;
            &:focus,
            &:active,
            &:hover,
            &:focus-visible,
            &:focus-within,
            &:mousedown {
                outline: none !important;
                border: none !important;
                box-shadow: none !important;
            }
        }
        .sp-tab-button[data-active="true"] {
            background-color: ${Colors.grey8};
            border-radius: 6px;
            height: 32px;
        }
    }
`;

const SINGLE_FILE_WRAPPER = (htmlContent: string) => `
import { useEffect } from 'react';

export default function App(): JSX.Element {
  useEffect(() => {
    // Create and inject a style element to make the host page truly fullscreen.
    // This removes the default body margin and hides any potential scrollbars.
    const style = document.createElement('style');
    style.id = 'game-wrapper-fullscreen-style'; // Use an ID for easy cleanup.
    style.textContent = \`
      body {
        margin: 0;
        overflow: hidden;
      }
    \`;
    document.head.appendChild(style);

    const root = document.getElementById('html-render-root');
    if (!root) return;

    const iframe = document.createElement('iframe');
    iframe.srcdoc = htmlContent;
    
    // Style the iframe to fill the entire viewport.
    iframe.style.width = '100vw';
    iframe.style.height = '100vh';
    iframe.style.border = 'none';

    root.innerHTML = '';
    root.appendChild(iframe);
    
    // Cleanup function: remove the injected style and the iframe.
    return () => {
      const injectedStyle = document.getElementById('game-wrapper-fullscreen-style');
      if (injectedStyle) {
        injectedStyle.remove();
      }
      if (root) {
        root.innerHTML = '';
      }
    };
  }, []);

  // This div is now just a mount point; the iframe itself is sized to the viewport.
  return <div id="html-render-root" />;
}
`.replace('htmlContent', JSON.stringify(htmlContent));

export type DeviceType = 'computer' | 'Mobile';
const MOBILE = { width: 360, height: 680 };

type ConsoleMethodName = 'log' | 'warn' | 'error' | 'info' | 'debug';

interface GamePreviewProps {
    config: any;
    gameId?: string;
    mode: GameModes;
    code: GithubFormat;
    onError?: (error: string) => void;
    onWarn?: (warning: string) => void;
    onLog?: (warning: string) => void;
    devicePreview?: DeviceType;
    onSaveCodeEdit?: (code: GithubFormat) => void;
}

const GamePreview: React.FC<GamePreviewProps> = ({ config, gameId, mode, code, onError, onWarn, onLog, devicePreview, onSaveCodeEdit }) => {

    // console.log("gamepreview entry", { config, gameId, mode, code, onError, onWarn, onLog, devicePreview, onSaveCodeEdit });

    const template = useMemo(() => {
        if ((code || []).length > 1) {
            if (code.some(item => item.path.includes('src/index.tsx'))) { // || item.path.includes('index.tsx')
                return 'phaser-react';
            } else {
                return 'phaser';
            }
        }
    }, [code]);

    const formattedCode = useMemo(() => {
        if (!code || !Object.keys(code).length) return formatCode(CONFIG_CODE(template), CodeFormat.GITHUB, CodeFormat.SANDPACK) as SandpackFormat;

        let transformedCode = code;

        if ((code || []).length === 1) {
            const htmlContentReal = transformedCode.find(item =>
                item.path === 'index.html'
            ).content;

            // console.log({ htmlContentReal });

            transformedCode = [...transformedCode, { path: '/App.tsx', content: SINGLE_FILE_WRAPPER(htmlContentReal) }];
        }
        
        const updatedCode = formatCode([...(code.length > 1 ? CONFIG_CODE(template) : []), ...transformedCode.map(x => 
            localTransformCode(x, code.length > 1, template)
        )], CodeFormat.GITHUB, CodeFormat.SANDPACK) as SandpackFormat;
        
        if (process.env.REACT_APP_ENVIRONMENT === 'dev' || process.env.REACT_APP_ENVIRONMENT === 'local') {
            console.log('sandpack format: ', updatedCode);
        }

        return updatedCode;
    }, [code, template]);
    const previewIframeRef = useRef<HTMLIFrameElement>(null);
    const [hasCodeChanges, setHasCodeChanges] = useState(false);

    const cmRef = useRef(null);

    // useEffect(() => {
    //     console.log('template:', template);
    // }, [template]);

    useEffect(() => {
        const previewIframe = document.querySelector('.sp-preview-iframe') as HTMLIFrameElement;
        if (previewIframe) {
            previewIframeRef.current = previewIframe;
        }
    }, []);

    useEffect(() => {
        if (!previewIframeRef.current) return;
        // console.log('sandbox preview iframe: ', previewIframeRef.current);
        previewIframeRef.current.sandbox.value = 'allow-forms allow-presentation allow-same-origin allow-scripts allow-downloads allow-pointer-lock';
        previewIframeRef.current.allow = 'accelerometer; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; clipboard-read; clipboard-write; xr-spatial-tracking;';
        // console.log('sandbox preview iframe: ', previewIframeRef.current);
    }, [previewIframeRef]);

    const handleSave = useCallback(() => {
        triggerSave();
    }, []);

    // Listen for code changes in the editor
    useEffect(() => {
        const handleCodeChange = () => {
            if (!hasCodeChanges) {
                setHasCodeChanges(true);
            }
        };

        // Find the code editor element and add event listeners
        const codeEditor = document.querySelector('.sp-code-editor');
        if (codeEditor) {
            codeEditor.addEventListener('input', handleCodeChange);
            codeEditor.addEventListener('keydown', handleCodeChange);
        }

        return () => {
            if (codeEditor) {
                codeEditor.removeEventListener('input', handleCodeChange);
                codeEditor.removeEventListener('keydown', handleCodeChange);
            }
        };
    }, [hasCodeChanges]);

    // Setup the global save handler reference
    useEffect(() => {
        return setupSaveHandler();
    }, []);

    useEffect(() => {
      if (!config.DEBUG_GAME_TABS) {
          return;
      }
      const cm = cmRef.current?.getCodemirror();
      if (cm && cm.refresh) {
        setTimeout(() => cm.refresh(), 0);
      }
    }, []);

    // Listen for messages from the iframe (captured logs)
    useEffect(() => {
        const handleIframeMessage = (event: MessageEvent) => {
            // console.log('DEBUG:', event.data);

            if (event.data.type === 'console' && event.data.log && event.data.log.length > 0) {
                event.data.log.forEach((logItem: { method: ConsoleMethodName; data: string[]; id: string }) => {
                    // console.log('debug inner', logItem);
        
                    if (logItem.method === 'warn') {
                        const warningContent = logItem.data.map(item => String(item)).join('\n');
                        // console.warn('IFRAME WARNING DETECTED:', warningContent);
                        if (onWarn) {
                            onWarn(warningContent);
                        }
                    } else if (logItem.method === 'log') {
                        const logContent = logItem.data.map(item => String(item)).join('\n');
                        if (!logContent.includes('Phaser v3.90.0 (WebGL | Web Audio)')) {
                            // console.log('IFRAME LOG DETECTED:', logContent);
                            if (onLog) {
                                onLog(logContent);
                            }
                        }
                    }
                });
            } else if (event.data.type === 'action' && event.data.action === 'show-error') {
                const { title, message, payload } = event.data;
                let errorContent = `Uncaught ${title}: ${message}`;
                const INCLUDE_ERROR_DETAIL = true;
        
                // Check if the error message contains the specific string (case-insensitive)
                if ((errorContent.toLowerCase().includes('framebuffer status: incomplete attachment')) ||
                    (errorContent.toLowerCase().includes('failed to start') && errorContent.toLowerCase().includes('audio device'))) {
                    // Do not call onError for this specific error
                    return;
                }
                
                const cleanFilePath = (fileName: string) => {
                    let cleaned = fileName;
        
                    // First, check if it's likely a node module path by looking for 'node_modules/'
                    const isNodeModule = cleaned.includes('node_modules/');
        
                    // Remove all instances of /node_modules/
                    cleaned = cleaned.replace(/\/node_modules\//g, '');
        
                    // Remove origin (e.g., https://example.com)
                    cleaned = cleaned.replace(/^https?:\/\/[^/]+/, '');
        
                    // Remove leading slash if present after origin removal
                    if (cleaned.startsWith('/')) {
                        cleaned = cleaned.substring(1);
                    }
        
                    // If it's a node module, also remove '/<whatever>/dist/' or '/dist/'
                    // This regex looks for:
                    //   - a leading '/' (optional, as it might have been removed already)
                    //   - then any characters that are not a '/' (the "whatever" part, captured in group 1)
                    //   - then '/dist/'
                    // We replace it with nothing.
                    if (isNodeModule) {
                        cleaned = cleaned.replace(/(^|\/)([^/]+\/)?dist\//g, '');
                    }
        
                    // If it's a local file, remove the /src/ prefix
                    if (cleaned.startsWith('src/')) {
                        cleaned = cleaned.substring(4);
                    }
        
                    return cleaned;
                };
        
                // Check if a file path belongs to local user code and not a node module
                const isLocalUserCode = (fileName) => {
                    // A local file for the user would typically be in 'src/'
                    // and would not contain 'node_modules'.
                    // It might also contain the codesandbox origin, so we check for '/src/'
                    // and explicitly exclude 'node_modules/'.
                    return fileName.includes('/src/') && !fileName.includes('node_modules/');
                };
        
                const stackTrace = payload && payload.frames
                    ? payload.frames.map((frame) => {
                        const functionName = frame.functionName || '(anonymous)';
                        const fileName = cleanFilePath(frame.fileName);
                        const lineNumber = frame.lineNumber;
                        const columnNumber = frame.columnNumber;
                        return `    at ${functionName} (${fileName}:${lineNumber}:${columnNumber})`;
                    }).join('\n')
                    : '';

                // Filter stack frames to show only those originating from local user code
                const filteredStackTrace = payload && payload.frames
                    ? payload.frames
                        .filter((frame) => isLocalUserCode(frame.fileName))
                        .map((frame) => {
                            const functionName = frame.functionName || '(anonymous)';
                            const fileName = cleanFilePath(frame.fileName);
                            const lineNumber = frame.lineNumber;
                            const columnNumber = frame.columnNumber;
                            return `    at ${functionName} (${fileName}:${lineNumber}:${columnNumber})`;
                        }).join('\n')
                    : '';
        
                let errorLocation = null;
                if (event.data.path) {
                    errorLocation = cleanFilePath(event.data.path);
                    if (event.data.line !== null && event.data.column !== null) {
                        errorLocation = ` at ${errorLocation}:${event.data.line}:${event.data.column}`;
                    } else {
                        errorLocation = ` at ${errorLocation}`;
                    }
                }
        
                if (INCLUDE_ERROR_DETAIL) {
                    if (payload && payload.frames && payload.frames.length > 0) {
                        errorContent += `\n${stackTrace}`;
                    } else if (event.data.path) {
                        errorContent += errorLocation;
                    }
                } else {
                    if (payload && payload.frames && payload.frames.length > 0) {
                        errorContent += `\n${filteredStackTrace}`;
                    } else {
                        errorContent += errorLocation;
                    }
                }
        
                // console.error('IFRAME ERROR DETECTED:', errorContent);
                if (onError) {
                    onError(errorContent);
                }
            }
        };        

        window.addEventListener('message', handleIframeMessage);

        return () => {
            window.removeEventListener('message', handleIframeMessage);
        };
    }, [onError, onWarn, onLog]);

    if (!Object.keys(code).length) {
        return null; // Don't render until we have valid code
    }

    return (
        <SandpackContainer data-mode={mode} $devicePreview={devicePreview}>
            <SandpackProvider
                theme="dark"
                files={formattedCode}
                template={"react-ts"}
                options={{
                    autorun: true,
                    autoReload: true,
                    activeFile: template === 'phaser-react' ? "src/index.tsx" : "index.ts",
                    bundlerURL: process.env.REACT_APP_SANDPACK_BUNDLER_URL,
                    experimental_enableServiceWorker: false

                }}
                onChange={() => setHasCodeChanges(true)}
                sandboxId={"1234"}
            >
                <SandpackPreview
                    showOpenInCodeSandbox={false}
                    showRefreshButton={false}
                    showNavigator={false}
                    title=""
                    tabIndex={0}
                />
                <SandpackCodeEditor
                    initMode="immediate"
                    readOnly={false}
                    showLineNumbers={true}
                    showInlineErrors={true}
                    wrapContent={true}
                    showTabs={true}
                    showRunButton={false}
                    ref={cmRef}
                    //onMount={() => {
                    //    setTimeout(() => cmRef.current?.getCodemirror().refresh(), 0);
                    //}}
                />
                <SaveHandler gameId={gameId} setHasCodeChanges={setHasCodeChanges} onSaveCodeEdit={onSaveCodeEdit} />
                <SaveButton hasCodeChanges={hasCodeChanges} onClick={handleSave} />
            </SandpackProvider>
        </SandpackContainer>
    );
};

export default GamePreview;
